import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { User, ShoppingBag, Gift, Settings, LogOut, Copy, Send, CreditCard, Star, X, ChevronRight, Download, Crown } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import VoucherCard from '../components/VoucherCard';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import api from '../api/client';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ── Membership Card Visual ────────────────────────────────────────────────────
function MembershipCardVisual({ membership }) {
  const isPlatinum = membership.tariff_name?.toLowerCase().includes('platinum') || membership.tariff_name?.toLowerCase().includes('wexclusive');
  if (isPlatinum) {
    return (
      <div className="rounded-2xl overflow-hidden shadow-2xl">
        <img src={`${API_BASE}/api/uploads/platinum_card.png`} alt="Platinum Card" className="w-full" data-testid="platinum-card-visual" />
        <div className="bg-[#2A2A2A] px-6 py-3 flex justify-between text-white">
          <div>
            <p className="text-white/50 text-xs">Card No</p>
            <p className="font-mono text-sm tracking-wider" data-testid="membership-card-number">{membership.card_number}</p>
          </div>
          <div className="text-right">
            <p className="text-white/50 text-xs">Points</p>
            <p className="font-bold text-lg text-[#D4AF37]" data-testid="membership-points">{Math.floor(membership.points).toLocaleString()}</p>
          </div>
          <div className="text-right">
            <p className="text-white/50 text-xs">Discount</p>
            <p className="font-bold text-lg text-[#D4AF37]">{membership.discount_percent}%</p>
          </div>
        </div>
      </div>
    );
  }
  const bg = membership.color || '#561D1A';
  return (
    <div className="rounded-2xl p-6 text-white shadow-2xl relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${bg} 0%, ${bg}dd 100%)` }}>
      <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-10" style={{ background: 'white', transform: 'translate(30%, -30%)' }} />
      <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full opacity-10" style={{ background: 'white', transform: 'translate(-30%, 30%)' }} />
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-white/70 text-xs uppercase tracking-widest">LuxeMall</p>
            <p className="text-white font-bold text-lg mt-0.5">{membership.tariff_name} Member</p>
          </div>
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <Star size={20} className="text-[#D4AF37]" fill="#D4AF37" />
          </div>
        </div>
        <p className="font-mono text-white/90 text-base tracking-widest mb-6" data-testid="membership-card-number">{membership.card_number}</p>
        <div className="flex justify-between items-end">
          <div>
            <p className="text-white/60 text-xs">Points Balance</p>
            <p className="text-white font-bold text-xl" data-testid="membership-points">{Math.floor(membership.points).toLocaleString()}</p>
          </div>
          <div className="text-right">
            <p className="text-white/60 text-xs">Discount</p>
            <p className="text-white font-bold text-xl">{membership.discount_percent}%</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Membership Tab ─────────────────────────────────────────────────────────────
function MembershipTab() {
  const { formatMoney, formatDate } = useCurrency();
  const [membership, setMembership] = useState(null);
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);

  const loadData = () => {
    Promise.all([
      api.get('/api/shop/membership').catch(() => ({ data: {} })),
      api.get('/api/shop/membership/tariffs').catch(() => ({ data: [] })),
    ]).then(([mRes, tRes]) => {
      setMembership(Object.keys(mRes.data).length > 0 ? mRes.data : null);
      setTariffs(tRes.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const handleEnroll = async (tariff_id) => {
    setEnrolling(tariff_id);
    try {
      const res = await api.post('/api/shop/membership/enroll', { tariff_id });
      setMembership(res.data);
      toast.success(`Enrolled in ${res.data.tariff_name} membership!`);
    } catch (err) {
      toast.error(err.displayMessage || 'Enrollment failed');
    } finally { setEnrolling(false); }
  };

  if (loading) return <div className="h-48 bg-gray-100 rounded-2xl animate-pulse" />;

  return (
    <div data-testid="membership-tab">
      {membership ? (
        <div className="max-w-md">
          <MembershipCardVisual membership={membership} />

          <div className="mt-5 bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
            <h3 className="font-bold text-[#561D1A] mb-4">Membership Details</h3>
            <div className="space-y-3 text-sm">
              {[
                ['Status', <span className="text-emerald-600 font-medium capitalize">{membership.status}</span>],
                ['Total Spent', formatMoney(membership.total_spent)],
                ['Points Balance', `${Math.floor(membership.points).toLocaleString()} pts`],
                ['Discount', `${membership.discount_percent}% off at participating stores`],
                ['Expires', formatDate(membership.expiry_date, { day: 'numeric', month: 'long', year: 'numeric' })],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between items-center py-2 border-b border-gray-50 last:border-0">
                  <span className="text-gray-500">{label}</span>
                  <span className="font-medium text-gray-800">{value}</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-gray-400 text-center">
              Present your Gift Voucher QR code at any participating store to redeem.
            </p>
          </div>
        </div>
      ) : (
        <div>
          <div className="bg-[#561D1A]/5 border border-[#561D1A]/10 rounded-2xl p-5 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-xl flex items-center justify-center"><Star size={20} className="text-[#D4AF37]" /></div>
              <div>
                <p className="font-bold text-[#561D1A]">Join the LuxeMall Loyalty Programme</p>
                <p className="text-gray-500 text-sm">Earn points, get discounts on every purchase</p>
              </div>
            </div>
          </div>
          {tariffs.length === 0 ? (
            <p className="text-center text-gray-400 py-8">No membership tiers available yet</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {tariffs.map(tariff => (
                <div key={tariff.id} className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm" data-testid="tariff-card">
                  <div className="h-2" style={{ background: tariff.color || '#561D1A' }} />
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-bold text-gray-800 text-base">{tariff.name}</h3>
                      <span className="font-bold text-[#561D1A]">
                        {tariff.price > 0 ? `${formatMoney(tariff.price)}/yr` : 'Free'}
                      </span>
                    </div>
                    <p className="text-gray-400 text-xs mb-3">{tariff.description}</p>
                    <ul className="space-y-1.5 mb-4">
                      {tariff.benefits?.slice(0, 3).map((b, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs text-gray-600">
                          <div className="w-1.5 h-1.5 rounded-full" style={{ background: tariff.color || '#561D1A' }} />
                          {b}
                        </li>
                      ))}
                      {tariff.discount_percent > 0 && (
                        <li className="flex items-center gap-2 text-xs text-[#EC1A23] font-medium">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#EC1A23]" />
                          {tariff.discount_percent}% off all voucher purchases
                        </li>
                      )}
                    </ul>
                    <button
                      onClick={() => handleEnroll(tariff.id)}
                      disabled={enrolling === tariff.id}
                      className="w-full py-2.5 rounded-xl text-sm font-medium text-white transition-all disabled:opacity-60"
                      style={{ background: tariff.color || '#561D1A' }}
                      data-testid="enroll-btn"
                    >
                      {enrolling === tariff.id ? 'Enrolling...' : 'Enroll Now'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Profile Page ──────────────────────────────────────────────────────────
export default function ProfilePage() {
  const { user, logout } = useAuth();
  const { formatMoney, formatDate, formatDateTime } = useCurrency();
  const [tab, setTab] = useState('vouchers');
  const [vouchers, setVouchers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [sendModal, setSendModal] = useState(null);
  const [sendForm, setSendForm] = useState({ recipient_email: '', gift_message: '' });
  const [sending, setSending] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: user?.name || '', phone: user?.phone || '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [platinumStatus, setPlatinumStatus] = useState(null);

  useEffect(() => {
    if (tab === 'vouchers') loadVouchers();
    if (tab === 'purchases') loadPurchases();
  }, [tab]);

  useEffect(() => {
    api.get('/api/platinum/my-status').then(res => setPlatinumStatus(res.data)).catch(() => {});
  }, []);

  const loadVouchers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/shop/profile/vouchers');
      setVouchers(res.data.vouchers);
    } catch { toast.error('Failed to load vouchers'); }
    finally { setLoading(false); }
  };

  const loadPurchases = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/shop/profile/purchases');
      setPurchases(res.data.orders);
    } catch { toast.error('Failed to load purchases'); }
    finally { setLoading(false); }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put('/api/auth/profile', profileForm);
      toast.success('Profile updated');
    } catch { toast.error('Failed to update profile'); }
    finally { setSavingProfile(false); }
  };

  const handleSendVoucher = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await api.post(`api/shop/customer/vouchers/${sendModal.id}/send`, sendForm);
      toast.success(res.data.message);
      setSendModal(null);
      setSendForm({ recipient_email: '', gift_message: '' });
      loadVouchers();
    } catch (err) {
      toast.error(err.displayMessage || 'Failed to send voucher');
    } finally { setSending(false); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied!');
  };

  const tabs = [
    { id: 'vouchers', label: 'My Vouchers', icon: Gift },
    { id: 'purchases', label: 'Purchases', icon: ShoppingBag },
    { id: 'membership', label: 'Membership', icon: CreditCard },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[#FFFCF9]" style={{ fontFamily: 'Roboto, sans-serif' }}>
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 py-10">
        {/* Header card */}
        <div className="bg-[#561D1A] rounded-2xl p-6 mb-6 text-white flex items-center justify-between" data-testid="profile-header">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
              <User size={28} className="text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-bold text-xl">{user?.name}</h2>
                {platinumStatus?.is_platinum && (
                  <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-[#D4AF37]/30 to-[#F5E6A3]/30 text-[#D4AF37]" data-testid="platinum-badge">
                    <Crown size={10} /> PLATINUM
                  </span>
                )}
              </div>
              <p className="text-white/70 text-sm">{user?.email}</p>
              <span className="text-xs bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-0.5 rounded-full capitalize mt-1 inline-block">
                {user?.role}
              </span>
            </div>
          </div>
          <button onClick={logout} className="text-white/60 hover:text-white flex items-center gap-1 text-sm" data-testid="profile-logout">
            <LogOut size={16} /> Logout
          </button>
        </div>

        {/* Platinum QR Card */}
        {platinumStatus?.is_platinum && (
          <div className="bg-gradient-to-r from-[#1a1a1a] to-[#2d1f3d] rounded-2xl p-6 mb-6 border border-[#D4AF37]/20" data-testid="platinum-qr-card">
            <div className="flex items-center gap-6">
              <div className="bg-white rounded-xl p-2 shrink-0">
                <QRCodeSVG value={platinumStatus.qr_data || ''} size={100} fgColor="#1a1a1a" level="M" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <Crown size={18} className="text-[#D4AF37]" />
                  <h3 className="text-[#D4AF37] font-bold text-lg tracking-wider">PLATINUM MEMBER</h3>
                </div>
                {platinumStatus.card_number_formatted && (
                  <p className="text-white/80 text-sm font-mono tracking-[3px] mb-1" data-testid="platinum-card-number">
                    {platinumStatus.card_number_formatted}
                  </p>
                )}
                <p className="text-white/50 text-xs">{platinumStatus.name}</p>
                <p className="text-white/40 text-xs mt-1">
                  Valid until: {platinumStatus.expiry_date ? formatDate(platinumStatus.expiry_date) : 'N/A'}
                </p>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button
                onClick={async () => {
                  try {
                    const res = await api.get('/api/platinum/my-card', { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement('a'); a.href = url; a.download = 'WExclusive_Platinum_Card.png'; a.click();
                    window.URL.revokeObjectURL(url);
                    toast.success('Platinum card downloaded');
                  } catch { toast.error('Failed to download card'); }
                }}
                className="flex items-center gap-1.5 text-xs bg-[#D4AF37]/20 text-[#D4AF37] px-3 py-1.5 rounded-lg hover:bg-[#D4AF37]/30 transition-colors"
                data-testid="download-platinum-card-btn">
                <Download size={12} /> Download Card
              </button>
            </div>
          </div>
        )}

        {/* Tab Bar */}
        <div className="flex gap-1 mb-6 bg-white rounded-2xl p-1.5 shadow-sm border border-gray-100 overflow-x-auto">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-sm font-medium transition-all whitespace-nowrap min-w-fit ${
                tab === id ? 'bg-[#561D1A] text-white shadow-md' : 'text-gray-500 hover:text-[#561D1A]'
              }`}
              data-testid={`tab-${id}`}
            >
              <Icon size={15} />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>

        {/* My Vouchers */}
        {tab === 'vouchers' && (
          <div data-testid="vouchers-tab">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1,2,3].map(i => <div key={i} className="h-48 bg-gray-100 rounded-2xl animate-pulse" />)}
              </div>
            ) : vouchers.length === 0 ? (
              <div className="text-center py-16">
                <Gift size={48} className="text-gray-300 mx-auto mb-4" />
                <p className="text-gray-400 text-lg font-medium">No vouchers yet</p>
                <p className="text-gray-300 text-sm mt-1">Your purchased vouchers will appear here</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {vouchers.map(v => (
                  <div key={v.id} className="relative group">
                    <VoucherCard voucher={v} onClick={() => setSelectedVoucher(v)} />
                    {v.status === 'active' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setSendModal(v); setSendForm({ recipient_email: '', gift_message: '' }); }}
                        className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity bg-[#EC1A23] text-white text-xs px-2 py-1 rounded-lg flex items-center gap-1 shadow-md"
                        data-testid="send-voucher-btn"
                      >
                        <Send size={11} /> Gift
                      </button>
                    )}
                    {v.balance !== undefined && v.balance < v.amount && (
                      <div className="mt-1 px-2">
                        <div className="flex justify-between text-xs text-gray-500 mb-0.5">
                          <span>Balance</span>
                          <span className="font-medium">{formatMoney(v.balance)}</span>
                        </div>
                        <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-[#EC1A23] rounded-full" style={{ width: `${(v.balance / v.amount) * 100}%` }} />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Purchases */}
        {tab === 'purchases' && (
          <div data-testid="purchases-tab">
            {loading ? (
              <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />)}</div>
            ) : purchases.length === 0 ? (
              <div className="text-center py-16">
                <ShoppingBag size={48} className="text-gray-300 mx-auto mb-4" />
                <p className="text-gray-400">No purchases yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {purchases.map(order => (
                  <div key={order.id} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm" data-testid="purchase-item">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-medium text-sm text-gray-800">{order.items?.length || 0} voucher(s)</p>
                        <p className="text-xs text-gray-400 mt-0.5 font-mono">#{order.id}</p>
                        {order.discountApplied > 0 && (
                          <p className="text-xs text-emerald-600 mt-0.5">{order.discountApplied}% membership discount applied</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-xl text-[#561D1A]">{formatMoney(order.totalAmount)}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${order.paymentStatus === 'completed' ? 'bg-emerald-100 text-emerald-600' : 'bg-yellow-100 text-yellow-600'}`}>
                          {order.paymentStatus}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400">{formatDateTime(order.createdAt)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Membership */}
        {tab === 'membership' && <MembershipTab />}

        {/* Settings */}
        {tab === 'settings' && (
          <div data-testid="settings-tab" className="max-w-md">
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
              <h3 className="font-bold text-xl text-[#561D1A] mb-5">Account Settings</h3>
              <form onSubmit={handleProfileSave} className="space-y-5">
                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1.5">Full Name</label>
                  <input value={profileForm.name} onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none transition-colors bg-transparent" data-testid="settings-name-input" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1.5">Phone Number</label>
                  <input value={profileForm.phone || ''} onChange={e => setProfileForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="+254 7XX XXX XXX"
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none transition-colors bg-transparent" data-testid="settings-phone-input" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1.5">Email</label>
                  <input value={user?.email} disabled className="w-full border-b-2 border-gray-100 py-2 text-sm text-gray-400 bg-transparent cursor-not-allowed" />
                </div>
                <button type="submit" disabled={savingProfile}
                  className="w-full bg-[#EC1A23] hover:bg-[#C4121A] disabled:opacity-60 text-white py-3 rounded-full text-sm font-medium" data-testid="save-profile-btn">
                  {savingProfile ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* QR Modal */}
      {selectedVoucher && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="voucher-qr-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="h-1.5 bg-gradient-to-r from-[#561D1A] via-[#EC1A23] to-[#D4AF37] rounded-full mb-5" />
            {selectedVoucher.voucher_image_url && (
              <div className="mb-4 rounded-xl overflow-hidden border border-gray-100">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${selectedVoucher.voucher_image_url}`} alt="Voucher" className="w-full" data-testid="voucher-artwork-image" />
              </div>
            )}
            <h3 className="font-bold text-xl text-[#561D1A] text-center mb-1">{selectedVoucher.type_name}</h3>
            <p className="text-center text-2xl font-bold text-[#EC1A23] mb-1">
              {formatMoney(selectedVoucher.amount)}
            </p>
            {selectedVoucher.balance !== undefined && selectedVoucher.balance < selectedVoucher.amount && (
              <p className="text-center text-sm text-gray-500 mb-3">
                Balance: <span className="font-bold text-[#561D1A]">{formatMoney(selectedVoucher.balance)}</span>
              </p>
            )}
            {!selectedVoucher.voucher_image_url && (
              <div className="flex justify-center mb-4 p-4 bg-gray-50 rounded-xl">
                <QRCodeSVG value={selectedVoucher.qr_data || selectedVoucher.code} size={180} fgColor="#561D1A" level="M" data-testid="voucher-qr-code" />
              </div>
            )}
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 mb-4">
              <span className="font-mono text-sm text-gray-600 flex-1 text-center tracking-widest">{selectedVoucher.code}</span>
              <button onClick={() => copyCode(selectedVoucher.code)} className="text-gray-400 hover:text-[#561D1A]"><Copy size={14} /></button>
            </div>
            <p className="text-center text-xs text-gray-400 mb-4">Show this QR code at any participating store</p>
            <div className="flex gap-3">
              <button
                onClick={async () => {
                  try {
                    const res = await api.get(`/api/shop/customer/vouchers/${selectedVoucher.id}/pdf`, { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                    const a = document.createElement('a'); a.href = url;
                    a.download = `LuxeMall_Voucher_${selectedVoucher.code}.pdf`;
                    a.click(); window.URL.revokeObjectURL(url);
                    toast.success('Voucher card downloaded');
                  } catch { toast.error('Download failed'); }
                }}
                className="flex-1 border-2 border-[#D4AF37] text-[#D4AF37] py-2.5 rounded-full text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-[#D4AF37]/10 transition-colors"
                data-testid="download-voucher-pdf-btn"
              >
                <Download size={14} /> Download Card
              </button>
              {selectedVoucher.status === 'active' && (
                <button
                  onClick={() => { setSelectedVoucher(null); setSendModal(selectedVoucher); setSendForm({ recipient_email: '', gift_message: '' }); }}
                  className="flex-1 border-2 border-[#561D1A] text-[#561D1A] py-2.5 rounded-full text-sm font-medium flex items-center justify-center gap-1.5"
                >
                  <Send size={14} /> Gift
                </button>
              )}
              <button onClick={() => setSelectedVoucher(null)} className="flex-1 bg-[#561D1A] text-white py-2.5 rounded-full text-sm font-medium">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Send/Gift Modal */}
      {sendModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-4" data-testid="send-voucher-modal">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="h-1.5 bg-gradient-to-r from-[#561D1A] via-[#EC1A23] to-[#D4AF37] rounded-t-2xl" />
            <div className="p-6">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h3 className="font-bold text-xl text-[#561D1A] flex items-center gap-2"><Gift size={20} /> Gift Voucher</h3>
                  <p className="text-gray-400 text-sm mt-0.5">
                    {sendModal.type_name} · {formatMoney(sendModal.amount)}
                  </p>
                </div>
                <button onClick={() => setSendModal(null)} className="text-gray-400 hover:text-gray-600 p-1"><X size={18} /></button>
              </div>

              <div className="bg-[#561D1A]/5 rounded-xl p-4 mb-5 flex items-center gap-3">
                <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-xl flex items-center justify-center">
                  <Send size={18} className="text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#561D1A]">Send this voucher as a gift</p>
                  <p className="text-xs text-gray-500">The recipient will receive it in their My Vouchers tab</p>
                </div>
              </div>

              <form onSubmit={handleSendVoucher} className="space-y-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1.5">Recipient Email *</label>
                  <input
                    required type="email"
                    value={sendForm.recipient_email}
                    onChange={e => setSendForm(f => ({ ...f, recipient_email: e.target.value }))}
                    placeholder="friend@example.com"
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none transition-colors bg-transparent"
                    data-testid="recipient-email-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1.5">Gift Message (optional)</label>
                  <textarea
                    value={sendForm.gift_message}
                    onChange={e => setSendForm(f => ({ ...f, gift_message: e.target.value }))}
                    placeholder="Happy Birthday! Enjoy this gift..."
                    rows={3}
                    className="w-full border-2 border-gray-200 focus:border-[#561D1A] p-2.5 text-sm outline-none rounded-xl bg-transparent resize-none transition-colors"
                    data-testid="gift-message-input"
                  />
                </div>
                <button
                  type="submit" disabled={sending}
                  className="w-full bg-[#EC1A23] hover:bg-[#C4121A] disabled:opacity-60 text-white py-3 rounded-full text-sm font-medium flex items-center justify-center gap-2"
                  data-testid="confirm-send-btn"
                >
                  {sending ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending...</> : <><Send size={15} /> Send Gift</>}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
